import * as turf from '@turf/turf';
import { featureCollection, getType } from '@turf/turf';
import * as yup from 'yup';
import { geometryTypes } from '../constants/fields';

export const coordinatesSchema = yup.object().shape({
  lat: yup
    .number()
    .required('Required (eg. 42.14)')
    .min(-90, 'Minimum is -90')
    .max(90, 'Maximum is 90'),
  lng: yup
    .number()
    .required('Required (eg. -73.76)')
    .min(-180, 'Minimum is -180')
    .max(180, 'Maximum is 180'),
});

/**
 * Turns a coordinates object like { lng: -73.767363, lat: 42.149975 } into a
 * GeoJSON point feature.
 */
export const coordsObjToPoint = (coords) => {
  try {
    if (!coordinatesSchema.validateSync(coords)) {
      return null;
    }
    return turf.point([coords.lng, coords.lat]);
  } catch (e) {
    return null;
  }
};

/**
 * Returns a new LineString feature with reversed coordinates.
 */
export const reverseLineString = (feature) => {
  if (getType(feature) !== geometryTypes.LINE_STRING)
    throw new Error('Reverse LineString can only handle LineString features');
  const newFeature = turf.clone(feature);
  newFeature.geometry.coordinates = feature.geometry.coordinates
    .slice()
    .reverse();
  return newFeature;
};

export const emptyFeature = () => ({
  type: 'FeatureCollection',
  features: [],
});

export const emptyGeojson = (options = {}) => ({
  type: 'geojson',
  data: emptyFeature(),
  ...options,
});

/**
 * Converts a MultiPolygon feature to an array of Polygon features.
 */
export const multipolygonToPolygons = (
  feature,
  // By default, don't copy the properties of `feature` to each of the new features:
  { retainProperties = false } = {},
) => {
  if (getType(feature) !== 'MultiPolygon') {
    throw new Error('Feature must be MultiPolygon.');
  }

  return feature.geometry.coordinates.map((coords) => ({
    type: 'Feature',
    properties: retainProperties ? feature.properties : {},
    geometry: { type: 'Polygon', coordinates: coords },
  }));
};

/**
 * Checks `feature` for self-intersecting polygons and attempts to fix them.
 * Accepts a Polygon or MultiPolygon feature and returns a Polygon or
 * Multipolygon feature.
 */
export const fixSelfIntersections = (
  feature,
  // By default, retain the properties and id of the original feature:
  { retainPropertiesAndId = true } = {},
) => {
  try {
    if (!['Polygon', 'MultiPolygon'].includes(turf.getType(feature))) {
      throw new Error('Feature must be Polygon or MultiPolygon.');
    }

    const hasSelfIntersections = turf.kinks(feature).features.length > 0;

    // If no self intersections, return the original feature unchanged
    if (!hasSelfIntersections) return feature;

    // Otherwise, fix the self-intersections
    const fixedPolygons = turf.unkinkPolygon(feature).features;

    // Merge the fixed polygons into one MultiPolygon
    const merged = fixedPolygons.reduce((a, b) =>
      turf.union(featureCollection([a, b])),
    );

    // Copy over properties to new feature
    if (retainPropertiesAndId) {
      merged.properties = feature.properties;
      merged.id = feature.id;
    }

    return merged;
  } catch (err) {
    console.error(err);
    return feature;
  }
};
